import L from "leaflet";
import { renderToStaticMarkup } from "react-dom/server";
import React from "react";
import {
  IcLocation,
  IcLocationImage,
  IcTruckEnrouteMsw,
  IcTruckEnrouteMswSelected,
  IcTruckEcoVan,
  IcTruckEcoVanSelected,
  BrandIcWasteField,
  IcGarage,
  IcDisposalSite,
  IcHighPriority,
} from "@racoon/icons";
import { ConnectProto } from "@racoon/api";
import { STATUS_TONE_HEX, type StatusTone } from "@racoon/lib";

type GovTruckInfo = ConnectProto.GovTruckInfo;
type Location = ConnectProto.Location;
type DepotLocation = ConnectProto.GovDepotInfo;
/**
 * Cache to avoid renderToStaticMarkup on every call
 * Use WeakMap to key by component reference
 */
const iconCache = new WeakMap<React.ComponentType<any>, Map<string, string>>();

function renderIconCached(
  Component: React.ComponentType<Record<string, unknown>>,
  props?: Record<string, unknown>,
): string {
  let componentCache = iconCache.get(Component);
  if (!componentCache) {
    componentCache = new Map<string, string>();
    iconCache.set(Component, componentCache);
  }

  const propsKey = JSON.stringify(props || {});
  if (componentCache.has(propsKey)) return componentCache.get(propsKey)!;

  try {
    const markup = renderToStaticMarkup(React.createElement(Component, props));
    componentCache.set(propsKey, markup);
    return markup;
  } catch {
    return "";
  }
}

export interface TruckIconOptions {
  isSelected?: boolean;
  providerColor?: string;
  /** Government-owned truck: drawn with its own sprite, never provider-tinted. */
  isGovernment?: boolean;
}

export interface PickupIconOptions {
  isSelected?: boolean;
}

/**
 * Cache for colored truck sprites
 */
const coloredTruckCache = new Map<string, string>();

/**
 * Creates a truck icon with plate number label and rotation angle
 * Shows different icon based on selection state and supports provider color customization
 *
 * @param truck - Truck data with id, plateNumber, and optional angle
 * @param options - { isSelected?: boolean, providerColor?: string }
 * @returns Leaflet DivIcon with plate number label, rotation, and optional provider color
 *
 * @example
 * const icon = makeTruckIcon(
 *   { id: "1", plateNumber: "381BCJ01", angle: 45 },
 *   { isSelected: false, providerColor: "#007aff" }
 * );
 */
export function makeTruckIcon(truck: GovTruckInfo, options: TruckIconOptions = {}): L.DivIcon {
  const { isSelected = false, isGovernment = false } = options;
  const providerColor = isGovernment ? undefined : options.providerColor;
  const IconComponent = isGovernment
    ? isSelected
      ? IcTruckEcoVanSelected
      : IcTruckEcoVan
    : isSelected
      ? IcTruckEnrouteMswSelected
      : IcTruckEnrouteMsw;

  // Create cache key based on icon type and color
  const cacheKey = `${isGovernment ? "gov" : "msw"}_${isSelected ? "selected" : "default"}_${providerColor || "default"}`;

  let sprite: string;
  if (coloredTruckCache.has(cacheKey)) {
    sprite = coloredTruckCache.get(cacheKey)!;
  } else {
    // Get uncolored sprite from cache
    let svg = renderIconCached(IconComponent as React.ComponentType<Record<string, unknown>>);

    // Apply provider color - replace ONLY green colors
    if (providerColor) {
      svg = svg
        .replace(/#1A8B27/gi, providerColor)
        .replace(/#23BF36/gi, providerColor)
        .replace(/#63E583/gi, providerColor)
        .replace(/#34C759/gi, providerColor)
        .replace(/#17BA58/gi, providerColor);
    }

    sprite = svg;
    coloredTruckCache.set(cacheKey, sprite);
  }

  // Get plate number from truck data, fallback to id if not available
  const plateNumberValue = truck.truck?.plateNumber;
  const plateNumber = renderToStaticMarkup(
    React.createElement(React.Fragment, {}, plateNumberValue),
  );
  const angle = (truck.angle ?? 0) - 90;

  const html = `
    <div style="position:relative;display:inline-block;">
      <div style="
        background-color:#303338F2;color:#fff;
        padding:2px 8px;
        width:max-content;max-width:140px;
        border-radius:12px;font-size:11px;line-height:20px;
        position:absolute;top:-20px;left:50%;transform:translateX(-50%);
        white-space:nowrap;overflow:hidden;text-overflow:ellipsis;
        font-family:Inter,sans-serif;font-weight:500;
        display: ${isSelected ? "block" : "none"};
      ">${plateNumber}</div>
      <div style="display:flex;align-items:center;justify-content:center;transform:rotate(${angle}deg);">
        ${sprite}
      </div>
    </div>
  `;

  return L.divIcon({
    className: "!bg-transparent !border-none",
    html,
    iconSize: [48, 52],
    iconAnchor: [24, 26],
    popupAnchor: [0, -26],
  });
}

/** Pickup location enriched client-side with the jobs that belong to it. */
type PickupLocationWithJobs = {
  _jobs?: Array<{ status?: ConnectProto.JobStatus; fileReports?: unknown[] }>;
};

/** True when any of the location's jobs carries at least one file report. */
function hasPickupFileReports(location: PickupLocationWithJobs): boolean {
  return (location._jobs ?? []).some((job) => (job?.fileReports?.length ?? 0) > 0);
}

/**
 * Derives the pickup location's representative job status from its `_jobs`.
 *
 * A location can hold several jobs, so we collapse them to a single status by
 * priority: an unfinished attempt (NOT_COMPLETED) dominates, then any pending
 * job (CREATED/ASSIGNED/ENROUTE/ON_SERVICE), then a completed one. Locations
 * without jobs resolve to UNDEFINED (rendered gray).
 */
export function getPickupLocationStatus(location: PickupLocationWithJobs): ConnectProto.JobStatus {
  const statuses = (location._jobs ?? [])
    .map((job) => job?.status)
    .filter((status): status is ConnectProto.JobStatus => status != null);
  if (statuses.length === 0) return ConnectProto.JobStatus.UNDEFINED;

  const has = (status: ConnectProto.JobStatus) => statuses.includes(status);
  if (has(ConnectProto.JobStatus.NOT_COMPLETED)) return ConnectProto.JobStatus.NOT_COMPLETED;
  if (
    has(ConnectProto.JobStatus.CREATED) ||
    has(ConnectProto.JobStatus.ASSIGNED) ||
    has(ConnectProto.JobStatus.ENROUTE) ||
    has(ConnectProto.JobStatus.ON_SERVICE)
  ) {
    return ConnectProto.JobStatus.CREATED;
  }
  if (has(ConnectProto.JobStatus.COMPLETED) || has(ConnectProto.JobStatus.FORCE_COMPLETED)) {
    return ConnectProto.JobStatus.COMPLETED;
  }
  return statuses[0];
}

/**
 * Maps a job status to a marker/cluster color.
 * Completed = green, pending = blue, not completed = red, otherwise gray.
 */
export function getPickupStatusColor(status: ConnectProto.JobStatus, isSelected = false): string {
  const hex = STATUS_TONE_HEX[pickupTone(status)];
  return isSelected ? hex.strong : hex.fill;
}

// GovDash folds every job still in flight into one "pending" colour, CREATED included.
function pickupTone(status: ConnectProto.JobStatus): StatusTone {
  switch (status) {
    case ConnectProto.JobStatus.COMPLETED:
    case ConnectProto.JobStatus.FORCE_COMPLETED:
      return "done";
    case ConnectProto.JobStatus.CREATED:
    case ConnectProto.JobStatus.ASSIGNED:
    case ConnectProto.JobStatus.ENROUTE:
    case ConnectProto.JobStatus.ON_SERVICE:
      return "active";
    case ConnectProto.JobStatus.NOT_COMPLETED:
      return "failed";
    default:
      return "cancelled";
  }
}

/**
 * Creates a pickup location icon colored by its job status.
 * Colors: completed (green) | pending (blue) | not completed (red) | unknown (gray)
 * Uses the photo-pin icon (IcLocationImage) when the location has file reports,
 * otherwise the plain pin (IcLocation).
 *
 * @param location - Pickup location enriched with `_jobs`
 * @param options - { isSelected?: boolean }
 * @returns Leaflet DivIcon with appropriate color
 *
 * @example
 * const icon = makePickupLocationIcon(location, { isSelected: true });
 */
export function makePickupLocationIcon(
  location: PickupLocationWithJobs,
  options: PickupIconOptions = {},
): L.DivIcon {
  const { isSelected = false } = options;
  const color = getPickupStatusColor(getPickupLocationStatus(location), isSelected);
  const IconComponent = hasPickupFileReports(location) ? IcLocationImage : IcLocation;

  const html = renderIconCached(IconComponent as React.ComponentType<Record<string, unknown>>, {
    width: "24",
    height: "24",
  });

  const coloredHtml = html.replace(/fill="currentColor"/gi, `fill="${color}"`);

  return L.divIcon({
    className: "!bg-transparent !border-none",
    html: `<div style="position:relative;width:24px;height:24px;">${coloredHtml}</div>`,
    iconSize: [24, 24],
    iconAnchor: [12, 24],
    popupAnchor: [0, -24],
  });
}

export interface DisposalSiteIconOptions {
  isSelected?: boolean;
}

export interface DepotIconOptions {
  isSelected?: boolean;
}

/**
 * Creates a disposal site icon with the IcWasteField icon
 *
 * @param disposalSite - Disposal site data
 * @param options - { isSelected?: boolean }
 * @returns Leaflet DivIcon for disposal site
 *
 * @example
 * const icon = makeDisposalSiteIcon(
 *   { id: "1", name: "Site A" },
 *   { isSelected: false }
 * );
 */
export function makeDisposalSiteIcon(
  _disposalSite: Location,
  options: DisposalSiteIconOptions = {},
): L.DivIcon {
  const { isSelected = false } = options;
  const normalizedDisposalSiteName = (_disposalSite.name ?? "").trim().toLowerCase();
  const siteIcon = normalizedDisposalSiteName === "kwc" ? IcDisposalSite : BrandIcWasteField;

  const html = renderIconCached(siteIcon as React.ComponentType<Record<string, unknown>>, {
    width: "24",
    height: "24",
  });

  return L.divIcon({
    className: "!bg-transparent !border-none",
    html: `<div style="position:relative;width:24px;height:24px;${isSelected ? "filter: drop-shadow(0 0 8px rgba(245, 135, 67, 0.8));" : ""}">${html}</div>`,
    iconSize: [24, 24],
    iconAnchor: [12, 24],
    popupAnchor: [0, -24],
  });
}

/**
 * Creates a depot icon with the IcGarage icon
 *
 * @param depot - Depot data
 * @param options - { isSelected?: boolean }
 * @returns Leaflet DivIcon for depot
 *
 * @example
 * const icon = makeDepotIcon(
 *   { id: "1", name: "Depot A" },
 *   { isSelected: false }
 * );
 */
export function makeDepotIcon(_depot: DepotLocation, options: DepotIconOptions = {}): L.DivIcon {
  const { isSelected = false } = options;

  const html = renderIconCached(IcGarage as React.ComponentType<Record<string, unknown>>, {
    width: "24",
    height: "24",
  });
  return L.divIcon({
    className: "!bg-transparent !border-none",
    html: `<div style="position:relative;width:24px;height:24px;${isSelected ? "filter: drop-shadow(0 0 8px rgba(0, 122, 255, 0.8));" : ""}">${html}</div>`,
    iconSize: [24, 24],
    iconAnchor: [12, 24],
    popupAnchor: [0, -24],
  });
}

export interface SuspiciousLocationIconOptions {
  isSelected?: boolean;
}

/**
 * Creates a suspicious location icon with the IcHighPriority icon
 * Color: red (#DC2626)
 *
 * @param suspiciousLocation - Suspicious location data
 * @param options - { isSelected?: boolean }
 * @returns Leaflet DivIcon for suspicious location
 *
 * @example
 * const icon = makeSuspiciousLocationIcon(
 *   { location: { id: "1", name: "Абая 345" }, total_stops: 5 },
 *   { isSelected: false }
 * );
 */
export function makeSuspiciousLocationIcon(
  _suspiciousLocation: ConnectProto.SuspiciousLocation,
  options: SuspiciousLocationIconOptions = {},
): L.DivIcon {
  const { isSelected = false } = options;
  const color = "#DC2626"; // red

  const html = renderIconCached(IcHighPriority as React.ComponentType<Record<string, unknown>>, {
    width: "24",
    height: "24",
  });

  // Replace currentColor with red
  const coloredHtml = html.replace(/fill="currentColor"/gi, `fill="${color}"`);

  return L.divIcon({
    className: "!bg-transparent !border-none",
    html: `<div style="position:relative;width:24px;height:24px;${isSelected ? "filter: drop-shadow(0 0 8px rgba(220, 38, 38, 0.8));" : ""}">${coloredHtml}</div>`,
    iconSize: [24, 24],
    iconAnchor: [12, 24],
    popupAnchor: [0, -24],
  });
}
